import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { urlParam } from '../lib/deepLink';
import { MASTER_PATH, masterKey, usePageTabs } from '../lib/pageTabs';
import { useSticky } from '../lib/sticky';
import { usePageActive } from '../lib/pageActive';
import { kindLabel } from '../lib/driverKind';
import { api, type ListArgs, type ListResult } from '../lib/api';
import {
  DataTable,
  ErrorBox,
  Pager, usePageSize,
  dash,
  useList,
  type Column,
  type Row,
} from '../components/DataTable';
import MasterForm from './MasterForm';
import { canScreen, useMe } from '../lib/me';
import type { MasterKind } from '../lib/api';
import Modal from '../components/Modal';
import CarExpirePanel from '../components/CarExpirePanel';
import CarTypeBulkForm from '../components/CarTypeBulkForm';
import ExternalAllocForm from '../components/ExternalAllocForm';
import AllocActivityPanel from '../components/AllocActivityPanel';
import CustDupPanel from '../components/CustDupPanel';
import OwnerDupPanel from '../components/OwnerDupPanel';
import { usePhone } from '../lib/phone';
import { useAutoSearch } from '../lib/search';

/** 쓰기가 열린 탭. 7화면 전부. */
const WRITABLE = new Set<string>(['grp', 'cust', 'owner', 'driver', 'car', 'garage', 'car_alloc']);

interface Tab {
  id: string;
  label: string;
  /** 검색창 안내문 — 탭마다 검색 대상이 다르다. */
  hint: string;
  /**
   * `scope='active'` 필터의 화면 라벨. 의미가 탭마다 달라서 문구도 다르게 쓴다
   * (차량=운용중 · 기사=재직중 · 편성=정산 진행 중). 없으면 필터 자체가 없는 탭이다.
   */
  scopeLabel?: string;
  /**
   * 두 번째 필터 — 편성의 「운행중」(종료일이 비었거나 오늘 이후). 레거시도 운행·정산을 따로 걸렀다
   * (`usp_CarAlloc_FindByFind` 의 `@DriveVal` · `@IsUse`). 기본 꺼짐.
   */
  runningLabel?: string;
  /** 내부/외부 거르기(편성 · 10-14 #237 송선우). */
  belongFilter?: boolean;
  columns: Column[];
  /** 엑셀 받기에서 표 칸 뒤에 붙일 칸(표엔 자리가 없어 뺀 것). 게시판 #61 — 사업계획서 대조용. */
  exportExtra?: { key: string; label: string }[];
  load: (args: ListArgs) => Promise<ListResult<Row>>;
}


/**
 * 표는 컬럼을 설정으로 받으므로 행을 느슨한 형태로 다룬다.
 * 각 API 의 구체 타입(Grp·Car·…)은 인덱스 시그니처가 없어 그대로는 못 넣는다 — 여기 한 곳에서만 넓힌다.
 */
const asRows = <T,>(p: Promise<ListResult<T>>) => p as unknown as Promise<ListResult<Row>>;

const yn = (v: unknown, yes: string, no: string) => (v === true ? yes : v === false ? no : '—');

/** `enddt` 는 빈 문자열이 "무기한"이다(NULL 아님). 그대로 '—' 로 두면 종료된 것처럼 읽힌다. */
const period = (row: Row) => {
  const s = dash(row.startdt);
  const raw = row.enddt;
  const e = raw === null || raw === undefined || raw === '' ? '무기한' : String(raw);
  return `${s} ~ ${e}`;
};

/** 등록·수정 시각 — KST 벽시계 문자열에서 분까지. */
const stamp = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v).slice(0, 16));
/** 등록자·수정자 — 웹 메일은 @ 앞만(레거시 로그인 ID 는 그대로). */
const who = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v).split('@')[0]);
/** 가액 — numeric 이 문자열로 온다(`161500000.0000`). 0·빈 값은 '—'. */
const moneyOrDash = (v: unknown) => (v === null || v === undefined || v === '' || Number(v) === 0 ? '—' : Math.round(Number(v)).toLocaleString('ko-KR'));

const card = (corp: unknown, num: unknown) => {
  const c = corp === null || corp === undefined || corp === '' ? null : String(corp);
  const n = num === null || num === undefined || num === '' ? null : String(num);
  if (!c && !n) return '—';
  return [c, n].filter(Boolean).join(' ');
};

/**
 * NO 칸 — 레거시와 같은 번호(PK). 담당자들이 번호로 서로 소통한다(2026-09-29 way 「각 기준정보도 ID 가 보여야 함」).
 * 눈에 띌 필요는 없고 찾을 때 읽히면 된다(배차 화면 NoCell 과 같은 모양). 정렬·칼럼 검색은 서버 COLS 에 이미 있다.
 */
// 폰에선 NO 를 빼고(`phone: 'hide'`) 탭마다 이름 칸을 왼쪽에 고정한다(DataTable `phone` · 2026-09-30 way 「기준정보·배차 표도」).
const noCol = (key: string): Column => ({
  key, label: 'NO', align: 'right', phone: 'hide',
  render: (r) => <span className="text-xs text-ink-soft">{dash(r[key])}</span>,
});
/** 다른 표의 번호 칸(편성의 차량·차주·기사·소속 NO). */
const idCol = (key: string, label: string): Column => ({ ...noCol(key), label });

const TABS: Tab[] = [
  {
    id: 'grp',
    label: '소속',
    hint: '소속 · 법인 · 담당자 · 전화 · 사업자번호',
    columns: [
      noCol('grp_id'),
      { key: 'grp_name', phone: 'pin', label: '소속명' },
      { key: 'corp_name', label: '법인명' },
      { key: 'grp_kind', label: '구분' },
      { key: 'biz_number', phone: 'hide', label: '사업자번호' },
      { key: 'manager', label: '담당자' },
      { key: 'phone', label: '연락처' },
      { key: 'addr', phone: 'hide', label: '주소' },
      { key: 'is_hidden', phone: 'hide', label: '사용', render: (r) => r.is_hidden ? <span className="text-ink-faint">숨김</span> : '사용' },
    ],
    // 숨긴 소속(#389)은 「사용중만」을 끄면 보인다 — 신규 접수 배차 소속에서도 빠진다.
    scopeLabel: '사용중만',
    load: (a) => asRows(api.listGrps(a)),
  },
  {
    id: 'cust',
    label: '거래처',
    hint: '거래처 · 코드 · 사업자번호 · 거래처담당자 · 전화 · 주소',
    columns: [
      noCol('cust_id'),
      { key: 'cust_name', phone: 'pin', label: '거래처명' },
      { key: 'cust_code', phone: 'hide', label: '코드' },
      { key: 'cust_kind', label: '구분' },
      { key: 'biz_number', phone: 'hide', label: '사업자번호' },
      { key: 'phone', label: '연락처' },
      { key: 'manager', label: '거래처담당자' },
      { key: 'manager_phone', label: '거래처담당자 연락처' },
      { key: 'address', phone: 'hide', label: '주소' },
      // 2026-08-02 — 거래처 정산에서 쓰는 값인데 화면에 없었다. 자리를 먹으면 칼럼 조절로 끈다.
      { key: 'cust_fin_manager', phone: 'hide', label: '거래처정산담당자' },
      { key: 'cust_fin_phone', phone: 'hide', label: '정산 연락처' },
      { key: 'drive_type', phone: 'hide', label: '운행구분' },
      { key: 'manage_type', phone: 'hide', label: '관리구분' },
    ],
    load: (a) => asRows(api.listCusts(a)),
  },
  {
    id: 'car',
    label: '차량',
    hint: '차량번호 · 차량명 · 차대번호 · 형식 · 차주 · 소속 · 차고지',
    scopeLabel: '운용중만',
    columns: [
      noCol('car_id'),
      { key: 'car_no', phone: 'pin', label: '차량번호', copy: (r) => String(r.car_no ?? '') },
      // #130 — 「차종」 → 「차량명」(레거시도 차 이름을 적던 칸이다).
      { key: 'car_name', label: '차량명' },
      // 자동차등록증 항목(2026-08-02). 자리를 많이 먹으므로 필요 없으면 칼럼 조절로 끈다.
      { key: 'ident_no', phone: 'hide', label: '차대번호' },
      { key: 'car_type', phone: 'hide', label: '형식' },
      { key: 'car_code', phone: 'hide', label: '형식코드' },
      { key: 'car_type_gubun', phone: 'hide', label: '유종·규격' },
      { key: 'full_cnt', phone: 'hide', label: '정원', align: 'right' },
      { key: 'car_year', phone: 'hide', label: '연식', align: 'right' },
      { key: 'grp_name', label: '소속' },
      { key: 'owner_name', label: '차주' },
      { key: 'garage_name', phone: 'hide', label: '차고지' },
      { key: 'ins_expire', label: '보험만기' },
      { key: 'test_expire', label: '검사만기' },
      {
        key: 'is_use',
        label: '상태',
        render: (r) =>
          r.is_use === true ? (
            <span className="text-ink">운용중</span>
          ) : (
            <span className="text-ink-faint">{dash(r.un_use_kind) === '—' ? '미운용' : String(r.un_use_kind)}</span>
          ),
      },
      // #130·#126(10-06) — 레거시 목록처럼 차량 정보 전부를 표에(종전엔 엑셀에만). 자리를 먹으면 칼럼 조절로 끈다.
      { key: 'owner_mobile', phone: 'hide', label: '차주 휴대폰' },
      { key: 'number_owner_name', phone: 'hide', label: '번호권리' },
      { key: 'number_price', phone: 'hide', label: '번호판 가액', align: 'right', render: (r) => moneyOrDash(r.number_price) },
      { key: 'car_cost', phone: 'hide', label: '차량가액', align: 'right', render: (r) => moneyOrDash(r.car_cost) },
      { key: 'car_date', phone: 'hide', label: '최초등록일' },
      { key: 'car_expire', phone: 'hide', label: '차령만료일' },
      { key: 'ins_valid', phone: 'hide', label: '보험유효기간' },
      { key: 'car_number_area', phone: 'hide', label: '번호판 지역' },
      { key: 'paint', phone: 'hide', label: '도색' },
      { key: 'gps', phone: 'hide', label: 'GPS번호' },
      { key: 'device_no', phone: 'hide', label: '단말기번호' },
      { key: 'un_use_date', phone: 'hide', label: '미운용일' },
      { key: 'un_use_comment', phone: 'hide', label: '미운용 메모' },
      { key: 'created_at', phone: 'hide', label: '등록일', render: (r) => stamp(r.created_at) },
      { key: 'created_by', phone: 'hide', label: '등록자', render: (r) => who(r.created_by) },
      { key: 'updated_at', phone: 'hide', label: '수정일', render: (r) => stamp(r.updated_at) },
      { key: 'updated_by', phone: 'hide', label: '수정자', render: (r) => who(r.updated_by) },
    ],
    load: (a) => asRows(api.listCars(a)),
  },
  {
    id: 'owner',
    label: '차주',
    hint: '차주 · 소속 · 전화 · 사업자번호 · 은행 · 이메일',
    columns: [
      noCol('owner_id'),
      { key: 'owner_name', phone: 'pin', label: '차주명' },
      { key: 'belong', label: '내부/외부' },
      { key: 'mobile_phone', label: '휴대전화' },
      { key: 'phone', phone: 'hide', label: '전화' },
      { key: 'biz_number', phone: 'hide', label: '사업자번호' },
      { key: 'bank_name', phone: 'hide', label: '은행' },
      { key: 'bank', phone: 'hide', label: '계좌' },
      { key: 'comment', phone: 'hide', label: '정산 메모' },
      { key: 'email', phone: 'hide', label: '이메일' },
    ],
    load: (a) => asRows(api.listOwners(a)),
  },
  {
    id: 'driver',
    label: '기사',
    hint: '기사 · 연락처 · 구분 · 면허종류',
    scopeLabel: '재직중만',
    columns: [
      noCol('driver_id'),
      { key: 'driver_name', phone: 'pin', label: '기사명' },
      { key: 'driver_kind', label: '기사구분', render: (r) => kindLabel(r.driver_kind) },
      { key: 'phone', label: '연락처' },
      // #398 비상연락망(연락처2 누락자 확인) — 항목관리·엑셀 받기에 같이 나간다
      { key: 'phone2', phone: 'hide', label: '연락처2' },
      // 차인지급액 조정 설정(09-28 회의). 비면 기본값 — 소속은 편성 소속, 소득은 4대보험.
      { key: 'pay_grp_name', phone: 'hide', label: '기사 소속(지급)', render: (r) => r.pay_grp_name ? String(r.pay_grp_name) : <span className="text-ink-faint">편성 소속</span> },
      { key: 'income_kind', phone: 'hide', label: '소득 구분', render: (r) => r.income_kind ? String(r.income_kind) : <span className="text-ink-faint">4대보험</span> },
      { key: 'lic_kind', phone: 'hide', label: '면허종류' },
      { key: 'lic_number', phone: 'hide', label: '면허번호' },
      { key: 'bus_lic_no', phone: 'hide', label: '버스자격증번호' },
      { key: 'date_drive_lic', phone: 'hide', label: '운전면허' },
      { key: 'date_bus_lic', phone: 'hide', label: '버스자격' },
      { key: 'date_bosu_edu', phone: 'hide', label: '보수교육' },
      { key: 'join_date', phone: 'hide', label: '입사일' },
      {
        key: 'is_resign',
        label: '재직',
        render: (r) =>
          r.is_resign === true ? (
            <span className="text-ink-faint">퇴사 {dash(r.resign_date) === '—' ? '' : `(${r.resign_date})`}</span>
          ) : (
            <span className="text-ink">재직</span>
          ),
      },
    ],
    load: (a) => asRows(api.listDrivers(a)),
  },
  {
    id: 'car_alloc',
    label: '운행차량편성',
    hint: '차량번호 · 기사 · 차주 · 소속 · 카드번호',
    scopeLabel: '정산 진행중만',
    runningLabel: '운행중만',
    belongFilter: true,
    columns: [
      noCol('car_alloc_id'),
      { key: 'car_no', phone: 'pin', label: '차량번호', copy: (r) => String(r.car_no ?? ''), render: (r) => (
          <span>
            {dash(r.car_no)}
            {r.car_deleted === true && <span className="ml-1 text-xs text-amber-600">(삭제된 차량)</span>}
          </span>
        ) },
      // 차량·차주·기사·소속 NO(10-20 way) — 재무추가 엑셀·과태료처럼 번호로 맞춰 볼 때
      idCol('car_id', '차량 NO'),
      { key: 'car_name', phone: 'hide', label: '차종' },
      { key: 'grp_name', phone: 'hide', label: '소속' },
      idCol('grp_id', '소속 NO'),
      // 내부/외부 = 편성 소속의 구분(외부 소속만 외부 · #237)
      { key: 'grp_kind', label: '내부/외부', render: (r) => r.grp_kind === '외부'
          ? <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800">외부</span>
          : <span className="text-xs text-ink-soft">내부</span> },
      { key: 'owner_name', label: '차주' },
      idCol('owner_id', '차주 NO'),
      {
        key: 'driver_name',
        label: '기사',
        render: (r) => (
          <span>
            {dash(r.driver_name)}
            {r.driver_missing === true && <span className="ml-1 text-xs text-red-600">(기사 없음)</span>}
            {r.driver_deleted === true && <span className="ml-1 text-xs text-amber-600">(삭제된 기사)</span>}
          </span>
        ),
      },
      idCol('driver_id', '기사 NO'),
      { key: 'driver_kind', phone: 'hide', label: '기사구분', render: (r) => kindLabel(r.driver_kind) },
      // 기사 연락처(10-07 way) — 번호가 겹치면 기사앱 인증번호가 안 간다. 같은 번호 편성을 여기서 찾는다(검색창에 숫자만 쳐도 걸린다).
      { key: 'driver_phone', phone: 'hide', label: '기사 연락처' },
      // 기사 소속(급여가 나가는 법인, 09-29 way) — 따로 정하지 않았으면 이 편성의 소속이 곧 기사 소속이라 흐리게 보인다.
      { key: 'pay_grp_name', phone: 'hide', label: '기사 소속(지급)', render: (r) => r.pay_grp_name ? String(r.pay_grp_name) : <span className="text-ink-faint">{dash(r.grp_name)}</span> },
      { key: 'period', phone: 'hide', label: '편성기간', render: period },
      { key: 'is_drive', label: '운행', render: (r) => yn(r.is_drive, '운행중', '종료') },
      { key: 'fuel', phone: 'hide', label: '주유카드', render: (r) => card(r.fuel_corp, r.fuel_num) },
      { key: 'hipass', phone: 'hide', label: '하이패스', render: (r) => card(r.hipass_corp, r.hipass_num) },
      { key: 'is_use', label: '정산', render: (r) => yn(r.is_use, '진행중', '종료') },
    ],
    load: (a) => asRows(api.listCarAllocs(a)),
  },
  {
    id: 'garage',
    label: '차고지',
    hint: '차고지명 또는 주소',
    columns: [
      noCol('garage_id'),
      { key: 'garage_name', phone: 'pin', label: '차고지명' },
      { key: 'addr', label: '주소' },
      { key: 'lat', phone: 'hide', label: '위도' },
      { key: 'lng', phone: 'hide', label: '경도' },
    ],
    load: (a) => asRows(api.listGarages(a)),
  },
];

/** 세부 탭 [id, 이름] — 화면 찾기(#396 · App.tsx)가 쓴다. */
export const MASTER_TABS: [string, string][] = TABS.map((t) => [t.id, t.label]);

export default function MasterData() {
  /**
   * 세부 탭(소속·거래처·차량…)마다 **따로 화면 탭**(2026-10-27 way 「독립적으로 창 활성화 · 서로 크로스로 왔다갔다」 — 접수·배차와 같은 방식).
   * 세부 탭은 자기 주소의 `?tab=` 으로 정해진다. 세부 탭 누르기 = 그 화면 탭으로 — 열려 있으면 검색·정렬·쪽 그대로, 없으면 새로.
   */
  const { search: locSearch } = useLocation();
  const navigate = useNavigate();
  const pageTabs = usePageTabs();
  const urlTab = urlParam(locSearch, 'tab');
  const tabId = TABS.some((t) => t.id === urlTab) ? urlTab! : TABS[0]!.id;
  // 상단 메뉴로 「기준정보」를 다시 열 때 마지막에 본 세부 탭부터(App.tsx Pages).
  const [, setLastTab] = useSticky<string>('master.tab', tabId);
  const pageActive = usePageActive();
  useEffect(() => { if (pageActive) setLastTab(tabId); }, [pageActive, tabId, setLastTab]);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [runningOnly, setRunningOnly] = useState(false);
  const [belong, setBelong] = useState<'' | '내부' | '외부'>('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('master', 50);
  /**
   * 정렬·칼럼별 검색(2026-08-02 사용자 요청). **서버가 한다** — 목록은 한 페이지(50행)뿐이라
   * 화면에서 정렬하면 "전체를 이름순으로"가 아니라 "이 50행만"이 되고, 그 차이는 화면에 안 보인다.
   */
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  /** 칼럼 검색도 치는 대로 — 한 글자마다 쏘면 목록이 매번 다시 온다. 멎으면 적용한다. */
  const [filterDraft, setFilterDraft] = useState<Record<string, string>>({});
  useAutoSearch(JSON.stringify(filterDraft), () => { setFilters(filterDraft); setPage(0); });

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  const [form, setForm] = useState<{ row: Row | null } | null>(null);
  const [expireOpen, setExpireOpen] = useState(false);
  /** #210 유종·규격 일괄 — 열 때 지금 검색 결과 전체를 받아 둔다. */
  const [bulkRows, setBulkRows] = useState<Row[] | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  // 외부 편성 한 번에 등록(10-08 way) — 이름만 받고 차주·기사·차량·편성을 양식대로.
  const [extOpen, setExtOpen] = useState(false);
  // 거래처 중복 정리(10-17 way) — 사업자번호 같고 이름 다른 묶음을 대표로 합친다.
  const [dupOpen, setDupOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  // 운행차량편성 — 한 번 눌러 고른 줄(아래 내역 탭 #300). 폰은 한 번 누름이 수정 창이라 쓰지 않는다.
  const phone = usePhone();
  const [picked, setPicked] = useState<Row | null>(null);
  const allocPanel = tabId === 'car_alloc' && !phone;

  const { me } = useMe();
  // 소속은 슈퍼관리자만 등록·수정(10-30 way · 서버 saveGrp 와 같은 판정)
  const canWrite = canScreen(me, 'master') && WRITABLE.has(tabId) && (tabId !== 'grp' || me?.role === 'admin');

  const tab = useMemo(() => TABS.find((t) => t.id === tabId) ?? TABS[0]!, [tabId]);

  const { rows, loading, error, total } = useList(
    () =>
      tab.load({
        q: q || undefined,
        limit: PAGE,
        offset: page * PAGE,
        scope: tab.scopeLabel && !activeOnly ? 'all' : 'active',
        running: tab.runningLabel ? runningOnly : undefined,
        belong: tab.belongFilter && belong ? belong : undefined,
        sort: sort?.key,
        dir: sort?.dir,
        filters,
      }),
    [tab, q, page, PAGE, activeOnly, runningOnly, belong, sort, filters, nonce],
  );

  function switchTab(id: string) {
    if (id === tabId) return;
    const open = pageTabs.find((t) => t.key === masterKey(id));
    navigate(open ? `${open.loc.pathname}${open.loc.search}` : `${MASTER_PATH}?tab=${id}`);
  }

  /**
   * 엑셀 받기(게시판 #61) — 지금 걸린 검색·필터·정렬 그대로 **전부**. 서버 한 번 상한이 200줄이라 이어 받는다.
   * 칸 = 표 칸 순서 + `exportExtra`. 값은 화면 가공 없이 원래 값(상태 칸만 글자로).
   */
  const [exporting, setExporting] = useState(false);
  const [error2, setError2] = useState<string | null>(null);
  /** 지금 검색·필터·정렬 그대로 끝까지(서버 쪽 상한 200씩) — 엑셀 받기·유종 일괄(#210) 공용. */
  async function loadAll(): Promise<Row[]> {
    const all: Row[] = [];
    for (let off = 0; ; off += 200) {
      const r = await tab.load({
        q: q || undefined, limit: 200, offset: off,
        scope: tab.scopeLabel && !activeOnly ? 'all' : 'active',
        running: tab.runningLabel ? runningOnly : undefined,
        belong: tab.belongFilter && belong ? belong : undefined,
        sort: sort?.key, dir: sort?.dir, filters,
      });
      all.push(...r.rows);
      if (r.rows.length < 200) break;
    }
    return all;
  }

  async function openBulk() {
    setBulkLoading(true);
    setError2(null);
    try {
      setBulkRows(await loadAll());
    } catch (e) {
      setError2(e instanceof Error ? e.message : String(e));
    } finally {
      setBulkLoading(false);
    }
  }

  async function exportXlsx() {
    setExporting(true);
    setError2(null);
    try {
      const all = await loadAll();
      const cols: { key: string; label: string; render?: Column['render'] }[] = [
        ...tab.columns.map((c) => ({ key: c.key, label: typeof c.label === 'string' ? c.label : c.key, render: c.render })),
        ...(tab.exportExtra ?? []),
      ];
      const cell = (r: Row, c: (typeof cols)[number]) => {
        if (c.key === 'is_use' && tab.id === 'car') return r.is_use === true ? '운용중' : dash(r.un_use_kind) === '—' ? '미운용' : String(r.un_use_kind);
        // 화면이 글자로 만드는 칸(편성기간·카드·운행)은 그 글자 그대로 — 원래 값만 내보내면 비거나 뜻이 달라진다
        const shown = c.render?.(r);
        if (typeof shown === 'string') return shown === '—' ? '' : shown;
        const v = r[c.key];
        return v === null || v === undefined ? '' : typeof v === 'boolean' ? (v ? 'Y' : 'N') : (v as string | number);
      };
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cols.map((c) => c.label), ...all.map((r) => cols.map((c) => cell(r, c)))]), tab.label);
      const d = new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
      XLSX.writeFile(wb, `기준정보_${tab.label}_${d}.xlsx`);
    } catch (e) {
      setError2(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  }

  /** 중복 안내(10-17)의 「그 거래처 열기」 — 이름으로 찾아 같은 번호 줄을 연다. 못 찾으면 목록을 그 이름으로 거른다. */
  async function openCust(id: number, name: string) {
    try {
      const r = await tab.load({ q: name || String(id), limit: 50, offset: 0, scope: 'all' });
      const hit = r.rows.find((x) => Number(x.cust_id) === id);
      if (hit) { setForm({ row: hit }); return; }
    } catch { /* 아래에서 목록 검색으로 */ }
    setForm(null);
    setTerm(name);
    setQ(name);
    setPage(0);
  }

  function saved(msg: string) {
    setForm(null);
    setExtOpen(false);
    setNotice(msg);
    setNonce((n) => n + 1);
  }

  function search(e: React.FormEvent) {
    e.preventDefault();
    setQ(term.trim());
    setPage(0);
  }

  // 표는 검색칸 입력(term)과 따로 그린다 — 「전체」 쪽 크기면 수천 줄이라 한 글자마다 다시 그리면 수십 초 멈춘다(10-14 #241).
  const table = useMemo(() => (
        <DataTable
          columns={tab.columns}
          rows={rows}
          loading={loading}
          // 조회 권한도 줄을 눌러 상세·서류를 본다(폼이 보기 전용으로 뜬다). 고치기는 입력 권한만.
          onRowClick={(r) => setForm({ row: r })}
          // 두 번 눌러 열기 · 한 번 누름은 차량번호 복사(10-14 #241 정여진) · 운행차량편성은 줄 고르기(아래 내역 탭 #300)
          openOnDoubleClick
          onRowSelect={allocPanel ? setPicked : undefined}
          activeRow={allocPanel && picked ? (r) => r.car_alloc_id === picked.car_alloc_id : undefined}
          // ⚠️ 탭마다 칼럼이 다르다 — 키에 탭을 넣지 않으면 소속 설정이 차량 설정을 덮어쓴다.
          tableId={`master.${tabId}`}
          // 기준정보에는 금액이 없다 — 합계는 "검색에 걸린 게 몇 건인가" 하나다.
          // 그게 곧 "지금 보이는 게 전부인가"에 대한 답이라 없으면 상한에 걸린 줄 모른다.
          total={total}
          // 정렬·칼럼별 검색. 같은 칸을 다시 누르면 오름 ↔ 내림, 다른 칸을 누르면 그 칸 오름차순.
          sort={sort}
          onSort={(key) => {
            setSort((cur) => (cur?.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
            setPage(0);
          }}
          filters={filterDraft}
          onFilter={(key, value) => setFilterDraft((cur) => ({ ...cur, [key]: value }))}
          // 검색어에 걸린 글자를 노랗게(#188 · 구 버스인과 같게).
          highlight={q}
        />
    ), [tab, rows, loading, tabId, total, sort, filterDraft, q, allocPanel, picked]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="page-head">
        <h1 className="font-bold">기준정보</h1>
        <p className="mt-1 text-sm text-ink-soft">
          {canWrite
            ? `줄을 두 번 누르면 수정할 수 있습니다 · 차량번호는 누르고 Ctrl+C 로 복사됩니다.${tabId === 'car_alloc' ? ' · 편성 줄을 한 번 누르면 아래에 내역이 보입니다.' : ''}`
            : '조회 권한입니다 — 줄을 두 번 누르면 상세·서류를 볼 수 있습니다(고치기는 입력 권한).'}
        </p>
      </div>

      <div className="btn-row-sm flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                t.id === tabId ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={search} className="ml-auto flex items-center gap-3">
          {/* 게시판 #62 — 검사 만료 60일 전·당일 차주 알림톡(보기는 누구나, 켜기·보내기는 관리자) */}
          {tabId === 'car' && (
            <button type="button" className="btn" onClick={() => setExpireOpen(true)}>
              검사 만료 알림
            </button>
          )}
          {canWrite && tabId === 'car' && (
            <button type="button" className="btn" onClick={() => void openBulk()} disabled={bulkLoading}
                    title="지금 검색 결과 차량에 유종·규격을 한 번에 넣습니다(#210)">
              {bulkLoading ? '불러오는 중…' : '유종·규격 일괄'}
            </button>
          )}
          {(tabId === 'cust' || tabId === 'owner') && (
            <button type="button" className="btn" onClick={() => setDupOpen(true)}
                    title={tabId === 'cust' ? '사업자번호는 같은데 이름이 다른 거래처를 대표 하나로 합칩니다'
                      : '사업자번호(관광사)·계좌(개인)가 같은 외부 차주를 대표 하나로 합칩니다'}>
              중복 정리
            </button>
          )}
          {canWrite && (tabId === 'car_alloc' || tabId === 'owner') && (
            <button type="button" className="btn" onClick={() => setExtOpen(true)}
                    title="관광사·개인 이름만 적으면 차주·기사·차량·편성이 양식대로 한 번에 만들어집니다">
              외부 편성 등록
            </button>
          )}
          {/* 외부 소속만 고치는 사람(10-29 영업)은 차고지 신규 없음 — 공용이라 서버가 막는다 */}
          {((canWrite && !(tabId === 'garage' && me?.masterGrpScope === 'external')) || (tabId === 'cust' && canScreen(me, 'dispatch'))) && (
            <button type="button" className="btn-primary" onClick={() => setForm({ row: null })}>
              신규 등록
            </button>
          )}
          {tab.scopeLabel && (
            <label className="flex cursor-pointer select-none items-center gap-1.5 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => {
                  setActiveOnly(e.target.checked);
                  setPage(0);
                }}
              />
              {tab.scopeLabel}
            </label>
          )}
          {tab.runningLabel && (
            <label className="flex cursor-pointer select-none items-center gap-1.5 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={runningOnly}
                onChange={(e) => {
                  setRunningOnly(e.target.checked);
                  setPage(0);
                }}
              />
              {tab.runningLabel}
            </label>
          )}
          {tab.belongFilter && (
            <select className="field w-auto py-1 text-sm" value={belong} aria-label="내부/외부"
              onChange={(e) => { setBelong(e.target.value as '' | '내부' | '외부'); setPage(0); }}>
              <option value="">내부·외부 전체</option>
              <option value="내부">내부만</option>
              <option value="외부">외부만</option>
            </select>
          )}
          <input
            className="field w-64"
            placeholder={tab.hint}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <button className="btn-ghost" type="submit">
            검색
          </button>
          <button className="btn-ghost" type="button" onClick={() => void exportXlsx()} disabled={exporting}>
            {exporting ? '받는 중…' : '엑셀 받기'}
          </button>
        </form>
      </div>

      {notice && (
        <div className="card flex items-center gap-3 border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <span>{notice}</span>
          <button className="ml-auto text-xs underline" onClick={() => setNotice(null)}>
            닫기
          </button>
        </div>
      )}

      {form && (
        // 차량·기사는 칸이 많아 넓게(#139 이력 칸 · #140 구 버스인 배치). 끌어서 옮길 수 있다(#139).
        <Modal width={tabId === 'car' || tabId === 'driver' ? 'max-w-6xl' : 'max-w-4xl'} draggable>
          <MasterForm
            kind={tabId as MasterKind}
            row={form.row}
            onClose={() => setForm(null)}
            onSaved={saved}
            onOpenCust={(id, name) => void openCust(id, name)}
            onOpenExternal={canWrite ? () => { setForm(null); setExtOpen(true); } : undefined}
          />
        </Modal>
      )}

      {extOpen && (
        <Modal width="max-w-4xl">
          <ExternalAllocForm onClose={() => setExtOpen(false)} onSaved={saved} />
        </Modal>
      )}

      {dupOpen && tabId === 'cust' && (
        <Modal width="max-w-6xl">
          <CustDupPanel canWrite={canWrite} onClose={() => setDupOpen(false)} onChanged={() => setNonce((n) => n + 1)} />
        </Modal>
      )}
      {dupOpen && tabId === 'owner' && (
        <Modal width="max-w-6xl">
          <OwnerDupPanel canWrite={canWrite} onClose={() => setDupOpen(false)} onChanged={() => setNonce((n) => n + 1)} />
        </Modal>
      )}

      {bulkRows && (
        <Modal width="max-w-3xl">
          <CarTypeBulkForm rows={bulkRows} onClose={() => setBulkRows(null)} onSaved={(m) => { setBulkRows(null); saved(m); }} />
        </Modal>
      )}

      {expireOpen && (
        <Modal width="max-w-4xl">
          <CarExpirePanel isAdmin={me?.role === 'admin'} onClose={() => setExpireOpen(false)} />
        </Modal>
      )}

      {error && <ErrorBox message={error} />}
      {error2 && <ErrorBox message={error2} />}

      {table}
      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
      {allocPanel && <AllocActivityPanel alloc={picked} />}
    </div>
  );
}
